import { useCallback, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { Props as DrugScreenWarningModalProps } from '@/components/DrugScreenWarningModal/types';
import PersonContext from '@/pages/Person/PersonContext';
import type { Encounter } from '@/types/encounter';
import { getDrugScreenTimeWindowsData } from '@/utils/drugScreenTimeWindows';

/**
 * Opens an encounter's drug screen form, asking for confirmation first when
 * the appointment is outside the window a screen is normally recorded in.
 * Must be used from the encounter page, since the form path is relative to it.
 */
const useRecordDrugScreen = (encounter?: Encounter) => {
  const location = useLocation();
  const navigate = useNavigate();
  const personContext = useContext(PersonContext);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { isInTheFuture, isWarningModalEnabled, startAt } = getDrugScreenTimeWindowsData(
    encounter?.appointment
  );
  const appointmentId = encounter?.appointment?.id;

  const goToDrugScreenForm = useCallback(
    () =>
      navigate(`./drug-screens/new?appointment-id=${appointmentId}`, {
        state: location.state
      }),
    [appointmentId, location.state, navigate]
  );

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const recordDrugScreen = useCallback(
    () => (isWarningModalEnabled ? setIsModalOpen(true) : goToDrugScreenForm()),
    [goToDrugScreenForm, isWarningModalEnabled]
  );

  // Only built when there's an appointment to warn about
  const warningModalProps: DrugScreenWarningModalProps | null =
    isWarningModalEnabled && encounter?.appointment?.start_at
      ? {
          appointmentStartAt: startAt,
          isAppointmentInTheFuture: isInTheFuture,
          isModalOpen,
          personTimeZone: personContext?.person?.time_zone ?? undefined,
          goToDrugScreenForm,
          onClose: closeModal
        }
      : null;

  return { canRecordDrugScreen: !!appointmentId, recordDrugScreen, warningModalProps };
};

export default useRecordDrugScreen;
